import { useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import { allTicketsKey, ss } from "consts";
import { AttendanceTicketModel, FormFieldModel } from "models";
import {
  AttBaseProviderType,
  PermissionAttProviderType,
  useAttendDetail,
} from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as AttendanceService from "services/attendance";
import { aytyFormatError } from "utils";

type TicketType = {
  form?: FormFieldModel[];
  list?: AttendanceTicketModel[];
};

type TicketContextType = {
  loading: boolean;
  tickets?: TicketType;
  getTickets: (isReload?: boolean) => Promise<void>;
};

const TicketContext = createContext({} as TicketContextType);

const Provider = ({ children, all }: AttBaseProviderType) => {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<TicketType>();
  const { pIdOmni } = useAttendDetail();
  const { error, warning } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (pIdOmni) {
      setLoading(true);
      setTickets(undefined);
    }
  }, [pIdOmni]);

  const getTickets = useCallback(
    async (isReload?: boolean) => {
      let current: TicketType = all[pIdOmni] ?? {};

      if (isReload || !current.list)
        await AttendanceService.getTicketList({ pIdOmni })
          .then(async ({ data }) => {
            if (data.idReturnAPI > 0) current.list = data.ticketList;
            else warning({ description: t(aytyFormatError(data)) });
          })
          .catch(errorsResolver.defaultError);

      if (!current.form)
        await AttendanceService.getFormTicket({ pIdOmni })
          .then(async ({ data }) => {
            if (data.idReturnAPI > 0) current.form = data.bindListTicket;
            else warning({ description: t(aytyFormatError(data)) });
          })
          .catch(errorsResolver.defaultError);

      all[pIdOmni] = current;

      setTickets({ ...current });
      ss.setItem(allTicketsKey, JSON.stringify(all));

      setLoading(false);
    },
    [all, pIdOmni, errorsResolver, warning, t]
  );

  return (
    <TicketContext.Provider value={{ loading, tickets, getTickets }}>
      {children}
    </TicketContext.Provider>
  );
};

export const TicketProvider = ({
  hasPermission = true,
  ...args
}: PermissionAttProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useTicket = () => useContext(TicketContext);
